/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */

import type { Dispatch, SetStateAction } from 'react';
import { formatMonthName } from './DateUtils';
import { showToastrMessage } from './Toastr.Utils';
import { CF_BASE_URL, DESIGNS_BASE_URL, FP_APP_URL, GROUP_ORDER_URL } from '@/constants/Config';
import type { DesignLicenseStatusTypes, HqLicensingStatusTypes } from '@/constants/Proof';
import type { IUser, UserUpdatePayload } from '@/common/interfaces/user.interface';
import {
  designLicensePriorityToStatusMap,
  designLicenseStatusToPriorityMap,
  designLicenseV4HqMap,
} from '@/constants/DesignLicense';

import { MONTHS } from '@/constants/Home';

/**
 * Method to return the initials of the user name.
 *
 * @param name - the full user name
 */
export const getInitials = (name: string): string => {
  if (!name) {
    return '';
  }

  const splittedUser = name.split(' ');

  if (splittedUser.length && splittedUser.length === 1) {
    return splittedUser[0].charAt(0).toUpperCase();
  }

  return `${splittedUser[0].charAt(0).toUpperCase()}${splittedUser[1].charAt(0).toUpperCase()}`;
};

/**
 * Method to filter out duplicates from an array of objects.
 *
 * @param list - array of objects
 * @param key - key to check for duplicates
 */
export const removeDuplicates = (list: any[], key: string) =>
  list.filter((obj, index) => index === list.findIndex((o) => obj[key] === o[key]));

/**
 * Method to filter out duplicates from an array of objects.
 *
 * @param list - array of objects
 * @param key - key to check for duplicates
 */
export const removeDuplicatesWithFromTop = (list: any[], key: string) => {
  const uniqueMap: { [key: string]: any } = {};
  const resultArray: Array<any> = [];
  const orderArray: number[] = [];

  list.forEach((item) => {
    if (!uniqueMap[item[key]]) {
      uniqueMap[item[key]] = item;
      resultArray.push(item);
      orderArray.push(item.id);
    } else {
      /** Find the index of the existing item in the resultArray and replace it */
      const index = orderArray.indexOf(item.id);
      resultArray[index] = item;
    }
  });

  return resultArray;
};

/**
 * Method to filter out an array of objects based on a key and value.
 *
 * @param list - array of objects
 * @param key - key to filter on
 * @param value - value to filter on
 */
export const filterListBasedOnKey = (list: any[], key: string, value?: string) =>
  [...list].filter((obj) => obj[key] === value);

/**
 * The method to return the account type of the user.
 *
 * @param user - user object.
 */
export const getAccountType = (user: UserUpdatePayload) => {
  if (user.schoolName && user.organizationName) {
    return 'Collegiate';
  }

  if (user.business && user.title) {
    return 'Corporate';
  }

  if (!user.schoolName && user.organizationName) {
    return 'Other';
  }

  return '';
};

/** This method is to split the firtName and lastName from fullName
 *
 * @param userName - the name of the user to be split
 */
const splitUserName = (userName: string) => {
  let firstName = '';
  let lastName = '';

  if (userName) {
    const data = userName.split(' ');
    const firstNameList: string[] = [];
    const lastNameList: string[] = [];

    data.forEach((item, index) => {
      if (index === 0 && item) {
        firstNameList.push(item.trim());
      } else if (item) {
        lastNameList.push(item.trim());
      }
    });

    firstName = firstNameList.join();
    lastName = lastNameList.join(' ');
  }

  return {
    firstName,
    lastName,
  };
};

/**
 * The method to return the payload for the user update API.
 *
 * @param user - user object
 */
const body: UserUpdatePayload = {};
export const getUserPayload = (user: IUser) => {
  body.accessLevel = user.access_level;
  body.email = user.email;
  body.firstName = user.first_name;
  body.lastName = user.last_name;
  body.phone = user.phone;
  body.teamRoleId = user.team_role;
  body.clientManagerId = user.manager_id;
  body.position = user.position;
  body.graduationYear = user.school_year;

  return body;
};

/**
 * The method to return the payload for the user details update API.
 *
 * @param user - user object
 */
const userDetails: UserUpdatePayload = {};
export const getUserDetailsPayload = (user: UserUpdatePayload) => {
  const { firstName, lastName } = splitUserName(user.fullName || '');
  userDetails.email = user.email;
  userDetails.firstName = firstName;
  userDetails.lastName = lastName;
  userDetails.phone = user.phone;
  userDetails.gradYear = user.gradYear || null;
  userDetails.currentPassword = user.currentPassword || null;
  userDetails.newPassword = user.newPassword || null;
  userDetails.password = user.newPassword || null;

  return userDetails;
};

/**
 * Function that copies a string to clipboard.
 *
 * @param value - value to copy to clipboard.
 */
export const copyToClipboard = (value: string) => {
  navigator.clipboard.writeText(value);
};

/**
 *
 * @returns - The year options for the dropdown.
 */
export const getYearOptions = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(new Date());
  const currentYear = Number(
    parts.find((p) => p.type === 'year')?.value ?? new Date().getFullYear()
  );
  const month = Number(parts.find((p) => p.type === 'month')?.value ?? new Date().getMonth() + 1);
  const startYear = month >= 6 ? currentYear + 1 : currentYear;
  const years = [...Array(5)].map((_, i) => startYear + i);

  const options = [...years].map((year) => ({
    label: `${year}`,
    value: `${year}`,
  }));

  return { years, options };
};

/**
 * The method to return the payload for the mobile user details update API.
 *
 * @param formData - The current form data.
 * @param loggedInUser - Logged in user details.
 * @param accountType - The type of account
 */
export const getMobileUserDetailsPayload = (
  formData: UserUpdatePayload,
  loggedInUser: IUser,
  accountType: string | Dispatch<SetStateAction<string>>
) => {
  let payload: UserUpdatePayload = {};

  const mobileUserDetails = getUserDetailsPayload(formData);
  payload = { ...mobileUserDetails };

  payload.graduationYear = mobileUserDetails.gradYear;
  payload.teamRoleId = loggedInUser.team_role;
  payload.clientManagerId = loggedInUser.manager_id;
  payload.position = loggedInUser.position;
  payload.accessLevel = loggedInUser.access_level;

  if (accountType === 'Collegiate') {
    payload.schoolName = formData.schoolName;
    payload.schoolId = formData.schoolId;
    payload.organizationName = formData.organizationName;
    payload.schoolNameForIterable = null;
    payload.business = null;
    payload.title = null;
  }

  if (accountType === 'Corporate') {
    payload.schoolName = null;
    payload.schoolId = null;
    payload.organizationName = null;
    payload.schoolNameForIterable = null;
    payload.graduationYear = null;
    payload.position = null;
    payload.business = formData.business;
    payload.title = formData.title;
  }

  if (accountType === 'Other') {
    payload.schoolName = null;
    payload.schoolId = null;
    payload.organizationName = formData.organizationName;
    payload.schoolNameForIterable = null;
    payload.graduationYear = null;
    payload.position = null;
    payload.business = null;
    payload.title = null;
  }

  return payload;
};

/**
 * The method to return the formatted date in format 'MMM d'.
 * @param timestamp - string
 */
export const getMonthAndDate = (timestamp: string) => {
  const date = new Date(timestamp);
  const monthName = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ][date.getMonth()];
  const day = date.getDate();
  return `${formatMonthName(monthName)} ${day}`;
};

/**
 * The method to calculate the number of days left to the due date.
 * @param dueDate - string (in ISO 8601 format)
 */
export const getDueDate = (dueDate: string) => {
  const currentDate = new Date();
  const dueDateTime = new Date(dueDate);

  const timeDiff = dueDateTime.getTime() - currentDate.getTime();

  const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));

  return daysLeft;
};

/**
 * The method to sort the array by property.
 * @param array - array to be sorted.
 * @param property - property to sort by.
 */
export const sortByProperty = <T>(array: Array<T>, property: keyof T): Array<T> =>
  [...array].sort((a, b) => String(a[property]).localeCompare(String(b[property])));

/**
 * Method to return price with commas & dollar sign
 * @param price - value to be formatted
 */
export const currencyFormat = (price: number) => {
  const nPrice = price ? Number(price) : 0;

  if (!nPrice) return '$0.00';
  const abs = Math.abs(nPrice)
    .toFixed(2)
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  return nPrice < 0 ? `-$${abs}` : `$${abs}`;
};

/**
 * A method to check If the hex code has # infront of it. Otherwise this method will the #.
 *
 * @param hexCode - The hex code to check.
 */
export const validateColorHexCode = (hexCode: string) => {
  if (hexCode) {
    const isHashIncluded = hexCode.includes('#');

    if (isHashIncluded) {
      return hexCode;
    }

    return `#${hexCode}`;
  }

  return '';
};

/**
 * Method to check if the hex color has multiple values.
 *
 * @param hexColor - Selected hex color
 */
export const getBgColor = (hexColor: string) => {
  if (hexColor) {
    let splitedHexColor = hexColor.split(',');

    splitedHexColor = splitedHexColor.map((color) => validateColorHexCode(color));

    if (splitedHexColor.length === 2) {
      return `transparent linear-gradient(180deg, ${splitedHexColor[0]} 50%, ${splitedHexColor[1]} 50%) 0% 0% no-repeat padding-box`;
    }

    if (splitedHexColor.length === 3) {
      return `transparent linear-gradient(180deg, ${splitedHexColor[0]} 0%, ${splitedHexColor[0]} 33%, ${splitedHexColor[1]} 33%, ${splitedHexColor[1]} 67%, ${splitedHexColor[2]} 66%, ${splitedHexColor[2]} 100%)`;
    }

    return validateColorHexCode(hexColor);
  }

  return validateColorHexCode(hexColor);
};

/**
 * A method to return the group order link.
 *
 * @param groupOrderId - The ID of the group order.
 */
export const getShareableDesignLink = (groupOrderId: string) =>
  `${GROUP_ORDER_URL}?groupOrderId=${groupOrderId}`;

/**
 * Utility Service share the url on social media.
 */
export const shareOnSocialMedia = (media: string, textToShare: string, shareableUrl: string) => {
  if (media === 'instagram') {
    const twitterSharingURL = `https://www.instagram.com/shirtjustgotcrazy/?text=${textToShare}&url=${shareableUrl}&via=FreshPrintsLLC`;
    window.open(twitterSharingURL, 'TwitterShare', 'width=700,height=500');
  } else if (media === 'facebook') {
    const facebookSharerUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareableUrl}&t=${textToShare}`;
    window.open(facebookSharerUrl, 'TwitterShare', 'width=700,height=500');
  }
};

/**
 * A method to return UTC day, month, and year.
 *
 * @param date - The date to be displayed.
 */
export const getUtcDateString = (date: string, shortYear = false) => {
  const dateObj = new Date(date);
  let dd: string | number = dateObj.getUTCDate();
  let mm: string | number = dateObj.getUTCMonth() + 1;
  let yyyy = dateObj.getUTCFullYear();

  if (dd < 10) {
    dd = `0${dd}`;
  }

  if (mm < 10) {
    mm = `0${mm}`;
  }

  if (shortYear) {
    yyyy = Number(String(yyyy).slice(2));
  }

  const dateString = `${mm}/${dd}/${yyyy}`;

  return dateString;
};

/**
 * Method to download a file.
 *
 * @param url - URL of the file to be downloaded.
 * @param fileName - File name to be downloaded
 */
export const downloadFile = (url: string, fileName: string) => {
  const downloadLink = document.createElement('a');
  downloadLink.href = url;
  downloadLink.download = fileName;
  downloadLink.click();
};

/**
 * Truncates a string with an ellipsis in the middle (e.g. "Participants_1...90.csv").
 */
export const truncateMiddle = (value: string, maxLength: number): string => {
  if (maxLength < 4 || value.length <= maxLength) {
    return value;
  }

  const ellipsis = '...';
  const charsToShow = maxLength - ellipsis.length;
  const frontLength = Math.ceil(charsToShow / 2);
  const backLength = Math.floor(charsToShow / 2);

  return `${value.slice(0, frontLength)}${ellipsis}${value.slice(value.length - backLength)}`;
};

/**
 * A method to convert a date to locale date string.
 *
 * @param date - The date to be formatted.
 */
export const getLocaleDateString = (date: string) => {
  const convertedDate = new Date(date);
  const dd: number = convertedDate.getUTCDate();
  const mm: number = convertedDate.getUTCMonth();
  const yyyy: number = convertedDate.getUTCFullYear();
  let ddStr = 'th';

  if (dd === 1) {
    ddStr = 'st';
  } else if (dd === 2) {
    ddStr = 'nd';
  } else if (dd === 3) {
    ddStr = 'rd';
  } else {
    ddStr = 'th';
  }

  return `${MONTHS[mm]} ${dd}${ddStr}, ${yyyy}`;
};

export const getDesignShareLink = (designId: string) => `${FP_APP_URL}design?id=${designId}`;
export const getAllDesignShareLink = (userId?: number) => `${FP_APP_URL}favorites-page/${userId}`;
export const getProductShareLink = (styleCode: string, colorHex: string) =>
  `${FP_APP_URL}product?styleCode=${styleCode}&hex=${colorHex}`;
/**
 * Method to get the CSV file url.
 *
 * @param fileName - The name of the file.
 */
export const getCsvFileUrlGetter = (fileName: string) => {
  const baseUrl = `${`${CF_BASE_URL}order_csv`}/`;

  return baseUrl + encodeURIComponent(fileName);
};

/**
 * Gets the final design licensing status according to statuses passed.
 *
 * @param greekLicense - Greek license status
 * @param collegiateLicense - Collegiate license status
 */
export const getFinalDesignLicenseStatus = (
  greekLicense: DesignLicenseStatusTypes,
  collegiateLicense: DesignLicenseStatusTypes
): HqLicensingStatusTypes | null => {
  let finalStatus = null;

  const minPriority = Math.min(
    designLicenseStatusToPriorityMap[greekLicense],
    designLicenseStatusToPriorityMap[collegiateLicense]
  );

  const v4LicensingStatus = designLicensePriorityToStatusMap[minPriority];

  if (v4LicensingStatus in designLicenseV4HqMap) {
    finalStatus = designLicenseV4HqMap[v4LicensingStatus as keyof typeof designLicenseV4HqMap];
  }

  return finalStatus;
};

/*
 * A function that groups a json array based on a specific key.
 *
 * @param objects - array of objects to be grouped
 * @param key - the key to be grouped by
 *
 * Note: I need to use any as type due to the fact that object key and values will have different types.
 */
export const groupBy = (objects: any, key: string) =>
  objects.reduce((rv: any, x: any) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});

/**
 * Method to return the short month name.
 *
 * @param month - Month name.
 */
export const getShortMonthName = (month: string) => formatMonthName(month);

/**
 * A method to download the images based on the image name.
 *
 * @param image - Image name.
 */
export const downloadImages = async (image: string) => {
  try {
    const response = await fetch(image as string, {
      method: 'get',
      mode: 'cors',
      referrerPolicy: 'no-referrer',
    });
    const blobImage = await response.blob();
    const href = URL.createObjectURL(blobImage);

    const element = document.createElement('a');
    element.href = href;
    element.download = image?.toString().split('/').pop() as string;
    /** simulate link click */
    document.body.appendChild(element); /** Required for this to work in FireFox */
    element.click();
    document.body.removeChild(element);

    return true;
  } catch (err) {
    showToastrMessage('Failed to download the image.', 'failure');

    return false;
  }
};

/**
 * Method to create unique identifier.
 *
 * @param name - The name to be added to the unique identifier
 */
export const getUniqueId = (name?: string) => {
  if (name) {
    return `${name}-${Math.random().toString(36).substr(2, 9)}`;
  }
  return `${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * This method is used to get the S3 file path for designs.
 *
 * @param image - The image to be added to the design url.
 */
export const s3FileUrlGetter = (image: string | null) => `${DESIGNS_BASE_URL}${image}`;

/**
 * Function to get unique records based on a specified key.
 * @param records Array of objects with the specified key property.
 * @param key The key to use for uniqueness (e.g., 'id').
 */
export const getUniqueRecordsByProperty = <T>(
  records: T[],
  key: keyof T,
  variantKey?: keyof T
): T[] => {
  const uniqueRecordsMap = new Map<any, T>();
  records.forEach((record) => {
    let compositeKey;
    if (variantKey) {
      compositeKey = `${record[key]}_${record[variantKey]}`;
    } else {
      compositeKey = `${record[key]}`;
    }
    uniqueRecordsMap.set(compositeKey, record);
  });

  const uniqueRecords = Array.from(uniqueRecordsMap.values());

  return uniqueRecords;
};

export const formatPrice = (price: number | string) => {
  if (typeof price === 'number') {
    return price !== 0 ? currencyFormat(price) : '--';
  }

  return price;
};

export const isAlphanumeric = (str: string) => /^[a-zA-Z0-9_]+$/.test(str);

export const createLocalUrl = (file: File) => URL.createObjectURL(file);

export const getDaysRemaining = (targetDate: string): string => {
  const currentDate = new Date();
  const targetDateTime = new Date(targetDate);

  const timeDiff = targetDateTime.getTime() - currentDate.getTime();

  const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  return `${Math.abs(days)} ${Math.abs(days) === 1 ? 'Day ' : 'Days '}`;
};

export const getFormatedEstimatedDateRange = (ShipTypeDate: any) => {
  if (!ShipTypeDate) return 'TBD';
  const minDateMonth = ShipTypeDate.minDate?.dateInString.split(' ')[0];
  const minDateDay = ShipTypeDate.minDate?.dateInString.split(' ')[1];
  const maxDateMonth = ShipTypeDate.maxDate?.dateInString.split(' ')[0];
  const maxDateDay = ShipTypeDate.maxDate?.dateInString.split(' ')[1];
  if (minDateMonth === maxDateMonth) {
    return `${minDateMonth} ${minDateDay} - ${maxDateDay}`;
  }
  return `${minDateMonth} ${minDateDay} - ${maxDateMonth} ${maxDateDay}`;
};

export const getLatestRestockDate = (dates: { [key: string]: string }) => {
  if (!Object.values(dates).length) return undefined;
  return Object.values(dates).reduce((latest, current) => {
    if (!latest) return current;
    return new Date(current) > new Date(latest) ? current : latest;
  }, '');
};

/**
 * Resolves a static asset path to an absolute same-origin URL.
 * Avoids 404s from relative paths on nested routes and double-slash URL concatenation.
 */
export const resolveAssetUrl = (source: string | undefined): string => {
  if (!source) return '';
  if (/^(https?:|data:)/i.test(source)) return source;

  const assetPath = source.replace(/^\/+/, '');

  if (typeof window === 'undefined') {
    return `/${assetPath}`;
  }

  return new URL(assetPath, `${window.location.origin}/`).href;
};
